import { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, ArrowRight, Check, CircleDollarSign, FileText, Loader2, Upload, X } from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Pill } from "@/components/Music";
import { Money, Threshold, TrackPicker, money, serviceName } from "@/components/Royalty";
import { api, apiError, formatDate, formatDateTime } from "@/lib/api";
import { cn } from "@/lib/utils";

const GROUPS = [
  ["detail", "all", "Royalty detail — all services", "Every play line: service, plan, ISRC, rate and usage month."],
  ["summary", "all", "Work summary — all services", "The same money grouped by work."],
  ["detail", "one", "Royalty detail — one file per service", ""],
  ["summary", "one", "Work summary — one file per service", ""],
];

const ROLE_TEXT = {
  used: "The fullest version, so its lines are the ones kept.",
  covered: "Everything in here is already in the file we kept.",
  check: "Used only to check the totals.",
};

function roleOf(files) {
  if (files.some((f) => f.role === "used")) return "used";
  if (files.every((f) => f.role === "check")) return "check";
  return "covered";
}

function titleCase(s) {
  return (s || "").toLowerCase().replace(/(^|[\s(\-/])([a-z])/g, (m, a, b) => a + b.toUpperCase());
}

function MatchRow({ r, choice, onChoose, bmi }) {
  const [picking, setPicking] = useState(false);
  const chosen = choice === undefined ? (r.match === "title" ? r.track : null) : choice;
  const confirmed = choice !== undefined;
  return (
    <div className="py-3 border-t border-line">
      <div className="grid sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] gap-3 items-center">
        <div className="min-w-0">
          <div className="text-[13px] font-semibold truncate">
            {titleCase(r.title)} {r.artist && <span className="font-normal text-muted2">· {r.artist}</span>}
          </div>
          <div className="font-mono text-[11px] text-muted2">
            {bmi ? `BMI song no. ${r.song_code}` : String(r.song_code || "").startsWith("MRI") ? `ISRC ${r.mlc_isrc || "none"}` : `MLC · ${r.mlc_isrc || "no ISRC"}`} · <Money value={r.amount} places={bmi ? 2 : undefined} />
          </div>
        </div>
        <div className="min-w-0 flex items-center gap-2 text-[12.5px]">
          <ArrowRight className="w-3.5 h-3.5 text-muted2 shrink-0" />
          {chosen ? (
            <span className="min-w-0">
              <b className="truncate block">{chosen.title}</b>
              <span className="font-mono text-[11px] text-muted2">Catalog · {chosen.isrc || "no ISRC"}</span>
            </span>
          ) : (
            <span className="text-muted2">{choice === null ? "Leave unlinked" : "Nothing in your catalog matches"}</span>
          )}
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {r.match === "title" && r.track && !(confirmed && choice?.track_id === r.track.track_id) && (
            <Button size="sm" className="bg-brand hover:bg-brand-hover h-8" onClick={() => onChoose(r.track)}>
              <Check className="w-3.5 h-3.5 mr-1" /> Same song
            </Button>
          )}
          {confirmed && chosen && <Pill tone="good" className="h-8 px-2.5">Linked</Pill>}
          <Button size="sm" variant="outline" className="h-8" onClick={() => setPicking((v) => !v)}>
            {r.match === "title" ? "Pick another" : "Link a track"}
          </Button>
          {chosen && (
            <Button size="sm" variant="ghost" className="h-8 text-muted2" onClick={() => onChoose(null)} aria-label="Leave unlinked">
              <X className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>
      {picking && (
        <div className="mt-2 sm:max-w-md sm:ml-auto">
          <TrackPicker
            autoFocus
            onPick={(t) => {
              onChoose(t);
              setPicking(false);
            }}
          />
        </div>
      )}
    </div>
  );
}

function BmiPreview({ s, choices, setChoice }) {
  const m = s.matching;
  const f = s.files[0];
  const facts = [
    ["Report", "BMI Accrual Report"],
    ["Period", s.label],
    ["Report date", s.distribution_date ? formatDate(s.distribution_date) : "—"],
    ["Account", `${s.payee_mlc} · ${s.payee_name}`],
    ["Pages read", s.pages],
    ["Lines", `${s.line_count} lines · ${s.works} songs`],
  ];
  return (
    <div className="space-y-4">
      <section className="panel px-5 py-4 flex items-center gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-xl bg-sec-pro-light text-sec-pro flex items-center justify-center font-mono text-[12px] font-semibold shrink-0">PDF</div>
        <div className="min-w-0 flex-1">
          <div className="text-[14.5px] font-semibold truncate">{f?.filename}</div>
          <div className="text-[12.5px] text-muted2">Recognised as a BMI accrual report · performance royalties</div>
        </div>
        <div className="text-right">
          <span className="font-mono font-semibold text-[16px]">{money(s.total, { places: 2 })}</span>
          <div className="text-[12px] text-muted2">
            {s.works} songs · {s.usages.toLocaleString()} performances
          </div>
        </div>
      </section>
      {s.existing && (
        <div className="panel px-5 py-3 bg-brand-light/50 text-[13px]">
          You imported this report on {formatDateTime(s.existing.imported_at)}. Importing again <b>replaces</b> it — nothing is counted twice.
        </div>
      )}
      <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-4 items-start">
        <div className="space-y-4">
          <section className="panel p-5">
            <div className="eyebrow mb-1">What we read from the PDF</div>
            {facts.map(([k, v]) => (
              <div key={k} className="flex justify-between gap-3 py-2 border-t border-line text-[13px]">
                <span className="text-muted2">{k}</span>
                <span className="font-semibold text-right">{v}</span>
              </div>
            ))}
          </section>
          <section className="panel p-5">
            <div className="eyebrow mb-1">Does it add up?</div>
            <p className="text-[12.5px] text-muted2 mb-2">
              {s.agree ? "Our lines match the Totals row printed at the end of the report." : "Our lines don't match the Totals row on the report — check the PDF before importing."}
            </p>
            {s.checks.map((c) => {
              const fmt = (v) => (c.money ? money(v, { places: 2 }) : v.toLocaleString());
              const ok = Math.abs(c.total - c.printed) < 0.005;
              return (
                <div key={c.label} className="flex justify-between items-center gap-3 py-2 border-t border-line text-[13px] flex-wrap">
                  <span>{c.label}</span>
                  <span className="inline-flex items-center gap-3 font-mono text-[12.5px]">
                    <span className="text-muted2">PDF says {fmt(c.printed)}</span>
                    <span>we read {fmt(c.total)}</span>
                    {ok ? <Check className="w-3.5 h-3.5 text-stock-in" /> : <AlertTriangle className="w-3.5 h-3.5 text-gold" />}
                  </span>
                </div>
              );
            })}
          </section>
        </div>
        <div className="space-y-4">
          <section className="rounded-2xl border border-gold/30 bg-gold-light px-5 py-4 space-y-3">
            <div className="flex gap-3">
              <AlertTriangle className="w-[18px] h-[18px] text-gold shrink-0 mt-0.5" />
              <div className="text-[13px] leading-relaxed">
                <b>Filed as earned, not paid.</b> An accrual report is money BMI is still holding, so it's kept apart from money you've received and never added to your paid total.
              </div>
            </div>
            <Threshold amount={s.total} threshold={s.threshold} />
          </section>
          <section className="panel p-5">
            <div className="eyebrow mb-3">Linking the {s.works} songs to your catalog</div>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="rounded-xl bg-emerald-50 dark:bg-emerald-950/40 px-3.5 py-3">
                <div className="font-display text-[24px] font-semibold text-stock-in">{m.isrc}</div>
                <div className="text-[12.5px]">{m.by_number ? `Linked (${m.by_number} by BMI number)` : "Matched by title"}</div>
              </div>
              <div className="rounded-xl bg-gold-light px-3.5 py-3">
                <div className="font-display text-[24px] font-semibold text-gold">{m.title}</div>
                <div className="text-[12.5px]">Title on several tracks</div>
              </div>
              <div className="rounded-xl bg-surface px-3.5 py-3">
                <div className="font-display text-[24px] font-semibold text-muted2">{m.none}</div>
                <div className="text-[12.5px]">No track with that title</div>
              </div>
            </div>
            <p className="text-[12.5px] text-muted2">
              BMI reports don't carry ISRCs, so songs are matched by title. Each linked track then keeps its BMI song number, so later reports link by number.
              {m.review.length > 0 && " Link the rest here, or later from the song."}
            </p>
            {m.review.length > 0 && (
              <div className="mt-1 max-h-[420px] overflow-y-auto">
                {m.review.map((r) => (
                  <MatchRow key={r.song_code} r={r} bmi choice={choices[r.song_code]} onChoose={(t) => setChoice(r.song_code, t)} />
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function MriPreview({ s, choices, setChoice }) {
  const m = s.matching;
  const f = s.files[0];
  const sum = s.summary || {};
  const facts = [
    ["Statement", `${s.service} royalty statement`],
    ["Period", s.label.replace(`${s.service} · `, "")],
    ["Statement date", s.distribution_date ? formatDate(s.distribution_date) : "—"],
    ["Payee", s.payee_name || "—"],
    ["Lines", `${s.line_count} lines · ${s.works} song${s.works === 1 ? "" : "s"}`],
    [s.service === "TikTok" ? "Videos made with your songs" : "Uses", s.usages.toLocaleString()],
  ];
  return (
    <div className="space-y-4">
      <section className="panel px-5 py-4 flex items-center gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-xl bg-sec-dist-light text-sec-dist flex items-center justify-center font-mono text-[12px] font-semibold shrink-0">MRI</div>
        <div className="min-w-0 flex-1">
          <div className="text-[14.5px] font-semibold truncate">{f?.filename}</div>
          <div className="text-[12.5px] text-muted2">Recognised as a Music Reports statement · {s.service}</div>
        </div>
        <div className="text-right">
          <span className="font-mono font-semibold text-[16px]">{money(s.total)}</span>
          <div className="text-[12px] text-muted2">
            {s.works} song{s.works === 1 ? "" : "s"} · {s.label.replace(`${s.service} · `, "")}
          </div>
        </div>
      </section>
      {s.existing && (
        <div className="panel px-5 py-3 bg-brand-light/50 text-[13px]">
          You imported this statement on {formatDateTime(s.existing.imported_at)}. Importing again <b>replaces</b> it — nothing is counted twice.
        </div>
      )}
      <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-4 items-start">
        <div className="space-y-4">
          <section className="panel p-5">
            <div className="eyebrow mb-1">What we read from the statement</div>
            {facts.map(([k, v]) => (
              <div key={k} className="flex justify-between gap-3 py-2 border-t border-line text-[13px]">
                <span className="text-muted2">{k}</span>
                <span className="font-semibold text-right">{v}</span>
              </div>
            ))}
          </section>
          <section className="panel p-5">
            <div className="eyebrow mb-1">Does it add up?</div>
            <p className="text-[12.5px] text-muted2 mb-2">{s.agree ? "Our lines match the totals printed on the statement." : "Our lines don't match the totals printed on the statement — check the file before importing."}</p>
            {s.checks.map((c) => {
              const ok = Math.abs(c.total - c.printed) < 0.000001;
              return (
                <div key={c.label} className="flex justify-between items-center gap-3 py-2 border-t border-line text-[13px] flex-wrap">
                  <span>{c.label}</span>
                  <span className="inline-flex items-center gap-3 font-mono text-[12.5px]">
                    <span className="text-muted2">statement says {money(c.printed)}</span>
                    <span>we read {money(c.total)}</span>
                    {ok ? <Check className="w-3.5 h-3.5 text-stock-in" /> : <AlertTriangle className="w-3.5 h-3.5 text-gold" />}
                  </span>
                </div>
              );
            })}
            {sum.prior > 0 && (
              <p className="text-[12.5px] text-muted2 mt-2 pt-2 border-t border-line">
                The statement also pays {money(sum.prior)} held from earlier quarters (total remittance {money(sum.remit)}). That money isn't broken down by song, so only this quarter's {money(s.total)} is filed against your songs.
              </p>
            )}
          </section>
        </div>
        <div className="space-y-4">
          <section className="panel p-5">
            <div className="eyebrow mb-3">Linking the {s.works} songs to your catalog</div>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="rounded-xl bg-emerald-50 dark:bg-emerald-950/40 px-3.5 py-3">
                <div className="font-display text-[24px] font-semibold text-stock-in">{m.isrc}</div>
                <div className="text-[12.5px]">Linked by ISRC</div>
              </div>
              <div className="rounded-xl bg-gold-light px-3.5 py-3">
                <div className="font-display text-[24px] font-semibold text-gold">{m.title}</div>
                <div className="text-[12.5px]">Matched by title — check</div>
              </div>
              <div className="rounded-xl bg-surface px-3.5 py-3">
                <div className="font-display text-[24px] font-semibold text-muted2">{m.none}</div>
                <div className="text-[12.5px]">Not in your catalog</div>
              </div>
            </div>
            <p className="text-[12.5px] text-muted2">
              Songs are linked by the ISRC on the statement, like the MLC. Your share is split among your signed writers on each track, the same as MLC money.
              {m.review.length > 0 && " Link the rest here, or later from the song."}
            </p>
            {m.review.length > 0 && (
              <div className="mt-1 max-h-[420px] overflow-y-auto">
                {m.review.map((r) => (
                  <MatchRow key={r.song_code} r={r} choice={choices[r.song_code]} onChoose={(t) => setChoice(r.song_code, t)} />
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function StatementPreview({ s, month, setMonth, choices, setChoice }) {
  if (s.source === "BMI") return <BmiPreview s={s} choices={choices} setChoice={setChoice} />;
  if (s.source === "MRI") return <MriPreview s={s} choices={choices} setChoice={setChoice} />;
  const groups = GROUPS.map(([kind, scope, label, note]) => {
    const files = s.files.filter((f) => f.kind === kind && f.scope === scope);
    return { kind, scope, label, note, files };
  }).filter((g) => g.files.length);
  const m = s.matching;
  const shapes = groups.length;
  return (
    <div className="space-y-4">
      <section className="panel overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 flex-wrap">
          <div className="w-10 h-10 rounded-xl bg-brand-light flex items-center justify-center shrink-0">
            <CircleDollarSign className="w-5 h-5 text-brand" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[14.5px] font-semibold">
              {s.files.length} file{s.files.length === 1 ? "" : "s"} · {s.distribution_id ? `MLC ${s.label} statement` : "MLC statement — month not in the files"}
            </div>
            <div className="text-[12.5px] text-muted2">
              {s.distribution_id ? `Distribution ${s.distribution_id}` : "Work summary files don't say which statement they're from"}
              {s.distribution_date ? ` · paid ${formatDate(s.distribution_date)}` : ""} · {s.payee_name || "—"} · {s.payee_mlc}
              {shapes > 1 ? ` · downloaded ${shapes} different ways` : ""}
            </div>
          </div>
          <div className="text-right">
            <Money value={s.total} bold className="text-[16px]" />
            <div className="text-[12px] text-muted2">
              {s.works} works · {s.line_count} lines · {s.usages} plays
            </div>
          </div>
        </div>
        {!s.distribution_id && (
          <div className="px-5 py-3 border-t border-line bg-gold-light/50 flex items-center gap-3 flex-wrap">
            <AlertTriangle className="w-4 h-4 text-gold" />
            <span className="text-[13px] flex-1 min-w-[220px]">Which statement month is this? We guessed from the plays — the MLC usually pays about three months later.</span>
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 rounded-lg border border-line bg-card px-3 text-[13px]" aria-label="Statement month" />
          </div>
        )}
        {s.existing && (
          <div className="px-5 py-3 border-t border-line bg-brand-light/50 text-[13px]">
            You imported this statement on {formatDateTime(s.existing.imported_at)}. Importing again <b>replaces</b> it — nothing is counted twice.
          </div>
        )}
        <div className="hidden md:grid grid-cols-[28px_minmax(0,1.5fr)_120px_minmax(0,1.5fr)] gap-4 px-5 py-2.5 bg-surface/70 border-y border-line eyebrow text-[10.5px]">
          <span />
          <span>What we found</span>
          <span>Status</span>
          <span>Why</span>
        </div>
        {groups.map((g) => {
          const role = roleOf(g.files);
          const rows = g.files.reduce((a, f) => a + f.rows, 0);
          const svc = [...new Set(g.files.flatMap((f) => f.services))].map(serviceName);
          const why =
            g.scope === "one" && role === "covered"
              ? "Together they add up to the all-services file, line for line."
              : g.scope === "one" && role === "used"
              ? "No all-services file, so these are combined."
              : ROLE_TEXT[role];
          return (
            <div key={g.label} className="grid md:grid-cols-[28px_minmax(0,1.5fr)_120px_minmax(0,1.5fr)] gap-x-4 gap-y-1 items-center px-5 py-3.5 border-t border-line first-of-type:border-t-0">
              <span className={cn("w-7 h-7 rounded-lg flex items-center justify-center max-md:hidden", role === "used" ? "bg-emerald-50 dark:bg-emerald-950/40 text-stock-in" : "bg-surface text-muted2")}>
                <FileText className="w-3.5 h-3.5" />
              </span>
              <div className="min-w-0">
                <div className="text-[13.5px] font-semibold">{g.label}</div>
                <div className="text-[12px] text-muted2 mt-0.5">
                  {g.files.length} file{g.files.length === 1 ? "" : "s"} · {rows} {g.kind === "detail" ? "lines" : "rows"}
                  {g.scope === "one" ? ` · ${svc.join(", ")}` : ""}
                </div>
              </div>
              <div>{role === "used" ? <Pill tone="good">Used</Pill> : role === "check" ? <Pill tone="brand">Used as a check</Pill> : <Pill>Already covered</Pill>}</div>
              <div className="text-[12.5px] text-muted2 leading-relaxed">{why}</div>
            </div>
          );
        })}
      </section>

      <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-4 items-start">
        <section className="panel p-5">
          <div className="eyebrow mb-1">Do the versions agree?</div>
          <p className="text-[12.5px] text-muted2 mb-2">{s.agree ? "Every version adds up to the same money." : "They don't all agree — check the files before importing."}</p>
          {s.checks.map((c) => (
            <div key={c.label} className="flex justify-between items-center py-2 border-t border-line text-[13px] gap-3">
              <span>{c.label}</span>
              <span className="inline-flex items-center gap-2">
                <Money value={c.total} places={8} />
                {Math.abs(c.total - s.total) < 1e-6 ? <Check className="w-3.5 h-3.5 text-stock-in" /> : <AlertTriangle className="w-3.5 h-3.5 text-gold" />}
              </span>
            </div>
          ))}
          {s.files.length > 1 && Math.abs(s.naive_total - s.total) > 1e-9 && (
            <div className="mt-3 px-3 py-2.5 rounded-lg bg-brand-light text-[12.5px] leading-relaxed">
              Adding every file together would have shown <b>{money(s.naive_total)}</b> instead of <b>{money(s.total)}</b>.
            </div>
          )}
        </section>
        <section className="panel p-5">
          <div className="eyebrow mb-3">Linking the {s.works} works to your catalog</div>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className="rounded-xl bg-emerald-50 dark:bg-emerald-950/40 px-3.5 py-3">
              <div className="font-display text-[24px] font-semibold text-stock-in">{m.isrc}</div>
              <div className="text-[12.5px]">By ISRC</div>
            </div>
            <div className="rounded-xl bg-gold-light px-3.5 py-3">
              <div className="font-display text-[24px] font-semibold text-gold">{m.title}</div>
              <div className="text-[12.5px]">By title only</div>
            </div>
            <div className="rounded-xl bg-surface px-3.5 py-3">
              <div className="font-display text-[24px] font-semibold text-muted2">{m.none}</div>
              <div className="text-[12.5px]">Not in your catalog</div>
            </div>
          </div>
          {m.review.length > 0 ? (
            <>
              <p className="text-[12.5px] text-muted2">
                {m.title > 0 && "The MLC has a different ISRC for the title matches. Confirm them and their ISRC is saved on your track, so next month they link by themselves. "}
                You can also do this later from the work.
              </p>
              <div className="mt-1 max-h-[420px] overflow-y-auto">
                {m.review.map((r) => (
                  <MatchRow key={r.song_code} r={r} choice={choices[r.song_code]} onChoose={(t) => setChoice(r.song_code, t)} />
                ))}
              </div>
            </>
          ) : (
            <p className="text-[12.5px] text-muted2">Every work matched a track by ISRC.</p>
          )}
        </section>
      </div>
    </div>
  );
}

export default function RoyaltyImport() {
  const navigate = useNavigate();
  const input = useRef(null);
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [months, setMonths] = useState({});
  const [choices, setChoices] = useState({});
  const [include, setInclude] = useState({});

  const upload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const form = new FormData();
      files.forEach((f) => form.append("files", f));
      const { data } = await api.post("/royalties/preview", form);
      setPreview(data);
      setMonths(Object.fromEntries(data.statements.map((s) => [s.key, s.suggested_distribution || ""])));
      setChoices({});
      setInclude(Object.fromEntries(data.statements.map((s) => [s.key, true])));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };

  const chosen = useMemo(() => (preview ? preview.statements.filter((s) => include[s.key]) : []), [preview, include]);
  const missingMonth = chosen.some((s) => !s.distribution_id && !months[s.key]);
  const lineCount = chosen.reduce((a, s) => a + s.line_count, 0);
  const total = chosen.reduce((a, s) => a + s.total, 0);

  const commit = async () => {
    setBusy(true);
    try {
      const matches = {};
      Object.entries(choices).forEach(([code, t]) => {
        matches[code] = t ? t.track_id : "";
      });
      const { data } = await api.post("/royalties/commit", {
        job_id: preview.job_id,
        statements: preview.statements.map((s) => ({ key: s.key, include: !!include[s.key], distribution_id: months[s.key] || "" })),
        matches,
      });
      const first = data.statements[0];
      toast.success(
        data.statements.length === 1
          ? first.status === "accrued"
            ? `${first.source} ${first.label} accrual report imported — ${first.line_count} lines, ${money(first.total, { places: 2 })} accrued`
            : `${first.label} statement imported — ${first.line_count} lines, ${money(first.total)}`
          : `${data.statements.length} statements imported`
      );
      navigate(first ? `/royalties?source=${first.source}&statement=${first.statement_id}` : "/royalties");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout
      title="Import royalty statement"
      subtitle="Drop MLC files, BMI PDFs or Music Reports statements — each is recognised and filed under its own source"
      action={
        <Button asChild variant="outline">
          <Link to="/royalties">
            <ArrowLeft className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Royalties</span>
          </Link>
        </Button>
      }
    >
      {!preview && (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDrag(true);
          }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDrag(false);
            upload(e.dataTransfer.files);
          }}
          className={cn("panel border-2 border-dashed p-10 text-center transition-colors", drag ? "border-brand bg-brand-light/40" : "border-line")}
        >
          <div className="w-12 h-12 rounded-2xl bg-brand-light flex items-center justify-center mx-auto">
            {busy ? <Loader2 className="w-5 h-5 text-brand animate-spin" /> : <Upload className="w-5 h-5 text-brand" />}
          </div>
          <h2 className="font-display text-[20px] font-semibold mt-4">{busy ? "Reading your files…" : "Drop your royalty statements here"}</h2>
          <p className="text-[13.5px] text-muted2 mt-1.5 max-w-xl mx-auto leading-relaxed">
            <b>The MLC:</b> .tsv, .csv or .xlsx, as many as you like at once — work summary or royalty detail, all services or one file per service. Files from the same statement are
            recognised and each line is kept once. <b>BMI:</b> the accrual report PDF. <b>Music Reports:</b> the statement workbook (e.g. TikTok). Each file is filed under its own
            source, and nothing is saved until you confirm.
          </p>
          <input ref={input} type="file" multiple accept=".tsv,.csv,.txt,.xlsx,.pdf" className="hidden" onChange={(e) => upload(e.target.files)} />
          <Button className="mt-5 bg-brand hover:bg-brand-hover" disabled={busy} onClick={() => input.current?.click()}>
            Choose files
          </Button>
        </div>
      )}

      {preview && (
        <div className="space-y-6">
          {preview.statements.length > 1 && (
            <section className="panel px-5 py-4">
              <div className="text-[14px] font-semibold">These files hold {preview.statements.length} different statements</div>
              <div className="flex flex-wrap gap-2 mt-2">
                {preview.statements.map((s) => (
                  <label key={s.key} className="inline-flex items-center gap-2 h-8 px-3 rounded-full border border-line text-[12.5px] font-semibold cursor-pointer">
                    <input type="checkbox" checked={!!include[s.key]} onChange={(e) => setInclude((v) => ({ ...v, [s.key]: e.target.checked }))} />
                    {s.source} · {s.distribution_id ? s.label : "Month unknown"} · {money(s.total, s.source === "BMI" ? { places: 2 } : undefined)}
                  </label>
                ))}
              </div>
            </section>
          )}
          {preview.statements
            .filter((s) => include[s.key])
            .map((s) => (
              <StatementPreview
                key={s.key}
                s={s}
                month={months[s.key] || ""}
                setMonth={(v) => setMonths((m) => ({ ...m, [s.key]: v }))}
                choices={choices}
                setChoice={(code, t) => setChoices((c) => ({ ...c, [code]: t }))}
              />
            ))}
          {preview.skipped.length > 0 && (
            <section className="panel px-5 py-4">
              <div className="eyebrow mb-2">Skipped · {preview.skipped.length}</div>
              {preview.skipped.map((f) => (
                <div key={f.filename} className="text-[12.5px] py-1">
                  <span className="font-mono text-[11.5px]">{f.filename}</span> — <span className="text-muted2">{f.reason}</span>
                </div>
              ))}
            </section>
          )}
          <div className="flex justify-end gap-2 flex-wrap sticky bottom-3">
            <Button
              variant="outline"
              className="bg-card"
              onClick={() => {
                setPreview(null);
                setChoices({});
              }}
            >
              Start over
            </Button>
            <Button className="bg-brand hover:bg-brand-hover" disabled={busy || !chosen.length || missingMonth} onClick={commit}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              {missingMonth
                ? "Pick the statement month first"
                : chosen.length === 1 && chosen[0].status === "accrued"
                ? `Import report — ${lineCount} lines, ${money(total, { places: 2 })} accrued`
                : chosen.length > 1
                ? `Import ${chosen.length} statements — ${lineCount} lines`
                : `Import statement — ${lineCount} lines, ${money(total)}`}
            </Button>
          </div>
        </div>
      )}
    </Layout>
  );
}
